import { useState } from 'react';

import bytes from 'bytes';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';

import { useIntl } from 'src/hooks/intl';
import { RepositoryPrivilege } from 'src/sections/repository/types';
import { getSizeLimits } from 'src/selectors/repository-selectors';
import store from 'src/utils/store';

import urls from '../urls';

import messages from './large-repository-warning.i18n';

type LargeRepositoryWarningMessageProps = {
  repositoryAccessLevel?: RepositoryPrivilege;
  size?: number;
  userUuid: string | undefined;
  repositoryUuid: string;
};

const LargeRepositoryWarningMessage = ({
  size = 0,
  repositoryAccessLevel,
  userUuid,
  repositoryUuid,
}: LargeRepositoryWarningMessageProps) => {
  const LARGE_SIZE_MESSAGE_DISMISSED_LOCALSTORAGE_KEY = `large-message-dismissed::${userUuid}::${repositoryUuid}`;
  const [showMessage, dismissMessage] = useState(
    !store.get(LARGE_SIZE_MESSAGE_DISMISSED_LOCALSTORAGE_KEY)
  );
  const intl = useIntl();
  const sizeLimits = useSelector(getSizeLimits);

  const softLimit = bytes(sizeLimits.soft, { unitSeparator: ' ' });
  const hardLimit = bytes(sizeLimits.hard, { unitSeparator: ' ' });

  if (size >= sizeLimits.hard) {
    return (
      <SectionMessage
        appearance="error"
        title={intl.formatMessage(messages.hardLimitHeadingLarge)}
        actions={[
          <SectionMessageAction key="more">
            <a href={urls.external.reduceRepositorySize} target="_blank">
              <FormattedMessage {...messages.softLimitLearnMoreLink} />
            </a>
          </SectionMessageAction>,
        ]}
      >
        {repositoryAccessLevel === 'admin' ? (
          <FormattedMessage
            {...messages.hardLimitDescriptionAdmin}
            values={{ hardLimit }}
          />
        ) : (
          <FormattedMessage
            {...messages.hardLimitDescription}
            values={{ hardLimit }}
          />
        )}
      </SectionMessage>
    );
  }

  if (
    size >= sizeLimits.soft &&
    repositoryAccessLevel === 'admin' &&
    showMessage
  ) {
    return (
      <SectionMessage
        appearance="warning"
        title={intl.formatMessage(messages.softLimitHeading)}
        actions={[
          <SectionMessageAction key="more">
            <a href={urls.external.reduceRepositorySize} target="_blank">
              <FormattedMessage {...messages.softLimitLearnMoreLink} />
            </a>
          </SectionMessageAction>,
          <SectionMessageAction
            key=""
            onClick={() => {
              dismissMessage(false);
              store.setStrictlyNecessaryItem(
                LARGE_SIZE_MESSAGE_DISMISSED_LOCALSTORAGE_KEY,
                true
              );
            }}
          >
            <FormattedMessage {...messages.dismiss} />
          </SectionMessageAction>,
        ]}
      >
        <FormattedMessage
          {...messages.softLimitDescriptionAdmin}
          values={{ softLimit, hardLimit }}
        />
      </SectionMessage>
    );
  }

  return null;
};

export default LargeRepositoryWarningMessage;
